import { Fragment, useMemo, useState } from 'react'
import { Plus, Pencil, Trash2, EyeOff, Eye, FolderTree } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { TableSkeleton } from '@/components/ui/skeleton'
import {
  useCategories,
  buildTree,
  useDeleteCategory,
  useSetCategoryArchived,
  fetchCategoryTxCount,
} from '@/hooks/useCategories'
import { useConfirm } from '@/context/ConfirmProvider'
import { CategoryFormDialog } from '@/components/categories/CategoryFormDialog'
import { useT } from '@/i18n/I18nProvider'
import { useTranslateCategory } from '@/lib/categoryDisplay'
import { cn } from '@/lib/utils'
import type { Category } from '@/types/db'

export function CategoriesPage() {
  const { t } = useT()
  const tc = useTranslateCategory()
  const { data: categories, isLoading } = useCategories(true)
  const del = useDeleteCategory()
  const setArchived = useSetCategoryArchived()
  const confirm = useConfirm()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Category | null>(null)
  const [showArchived, setShowArchived] = useState(true)

  const visible = useMemo(
    () => (categories ?? []).filter((c) => showArchived || !c.archived),
    [categories, showArchived],
  )
  // Três blocos por TIPO: Despesas · Receitas · Investimentos (só se houver).
  const sections = useMemo(() => {
    const out = [
      { key: 'expense', label: t('mng.cat.expenses'), empty: t('mng.cat.emptyExpenses'), tree: buildTree(visible, 'expense') },
      { key: 'income', label: t('mng.cat.incomes'), empty: t('mng.cat.emptyIncomes'), tree: buildTree(visible, 'income') },
    ]
    const invest = buildTree(visible, 'investment')
    if (invest.length > 0)
      out.push({ key: 'investment', label: t('mng.cat.investments'), empty: t('mng.cat.emptyInvestments'), tree: invest })
    return out
  }, [visible, t])

  function openNew() {
    setEditing(null)
    setDialogOpen(true)
  }
  function openEdit(c: Category) {
    setEditing(c)
    setDialogOpen(true)
  }
  async function toggleArchived(c: Category) {
    try {
      await setArchived.mutateAsync({ id: c.id, archived: !c.archived })
      toast.success(c.archived ? t('mng.cat.enabledToast') : t('mng.cat.disabledToast'))
    } catch (e) {
      toast.error(t('mng.errChange'), { description: (e as Error).message })
    }
  }
  async function remove(c: Category) {
    let txs = 0
    try {
      txs = await fetchCategoryTxCount(c.id)
    } catch (e) {
      toast.error(t('mng.cat.errCheck'), { description: (e as Error).message })
      return
    }
    const children = (categories ?? []).filter((x) => x.parent_id === c.id).length
    const parts: string[] = []
    if (txs > 0) parts.push(t('mng.cat.delTxWarn', { count: txs }))
    if (children > 0) parts.push(t('mng.cat.delChildWarn', { count: children }))
    parts.push(t('mng.cat.delBudgetWarn'))
    const ok = await confirm({
      title: t('mng.deleteTitle', { name: c.name }),
      description: `${parts.join(' · ')}. ${t('mng.cat.deleteTail')}`,
      confirmText: t('common.delete'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync(c.id)
      toast.success(t('mng.cat.deleted'))
    } catch (e) {
      toast.error(t('mng.errDelete'), { description: (e as Error).message })
    }
  }

  function row(c: Category, indent: boolean) {
    return (
      <TableRow key={c.id} className={cn(c.archived && 'opacity-60')}>
        <TableCell className={cn('font-medium', indent && 'pl-10 font-normal')}>
          <span className="inline-flex items-center gap-2">
            {c.icon && <span>{c.icon}</span>}
            {tc(c.name)}
            {c.archived && (
              <Badge variant="outline" className="text-muted-foreground text-xs">
                {t('mng.disabledBadge')}
              </Badge>
            )}
          </span>
        </TableCell>
        <TableCell className="text-muted-foreground whitespace-nowrap">
          {c.parent_id ? t('mng.cat.sub') : t('mng.cat.main')}
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toggleArchived(c)}
              aria-label={c.archived ? t('mng.enable') : t('mng.disable')}
              title={c.archived ? t('mng.enable') : t('mng.disable')}
            >
              {c.archived ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => openEdit(c)} aria-label={t('common.edit')}>
              <Pencil className="size-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => remove(c)} aria-label={t('common.delete')}>
              <Trash2 className="text-destructive size-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('settings.categories')}
        actions={
          <>
            <Button variant="outline" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? t('mng.cat.hideArchived') : t('mng.cat.showArchived')}
            </Button>
            <Button onClick={openNew}>
              <Plus className="size-4" /> {t('mng.cat.new')}
            </Button>
          </>
        }
      />

      {isLoading ? (
        <Card>
          <CardContent className="p-0">
            <TableSkeleton rows={6} cols={3} />
          </CardContent>
        </Card>
      ) : (categories ?? []).length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={FolderTree}
              title={t('mng.cat.emptyTitle')}
              description={t('mng.cat.emptyDesc')}
              action={
                <Button onClick={openNew}>
                  <Plus className="size-4" /> {t('mng.cat.new')}
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        sections.map(({ key, label, empty, tree }) => (
          <Card key={key}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{label}</TableHead>
                      <TableHead>{t('mng.cat.level')}</TableHead>
                      <TableHead className="w-32" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tree.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-muted-foreground py-6 text-center">
                          {empty}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tree.map(({ parent, children }) => (
                        <Fragment key={parent.id}>
                          {row(parent, false)}
                          {children.map((c) => row(c, true))}
                        </Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      <CategoryFormDialog open={dialogOpen} onOpenChange={setDialogOpen} category={editing} />
    </div>
  )
}
